export type TreatmentPlanPriceRangeInput = {
  isPriceRange?: string | number | boolean | null
  price?: unknown
  minSalePrice?: unknown
  maxSalePrice?: unknown
}

export type TreatmentPlanPriceRange = {
  min: number
  max: number
}

export type TreatmentPlanPriceDisplay = {
  primary: number | null
  range: TreatmentPlanPriceRange | null
}

export type TreatmentPlanPriceRangeValidationError =
  | 'MIN_REQUIRED'
  | 'MIN_INVALID'
  | 'MIN_NEGATIVE'
  | 'MAX_REQUIRED'
  | 'MAX_INVALID'
  | 'MAX_NEGATIVE'
  | 'MIN_GREATER_THAN_MAX'

const toFiniteNumber = (value: unknown): number | null => {
  if (value === null || value === undefined || String(value).trim() === '') return null
  const number = Number(value)
  return Number.isFinite(number) ? number : null
}

export const isTreatmentPlanPriceRangeEnabled = (value: TreatmentPlanPriceRangeInput['isPriceRange']): boolean => (
  value === '1' || value === 1 || value === true
)

export const validateTreatmentPlanPriceRange = (
  item: TreatmentPlanPriceRangeInput
): TreatmentPlanPriceRangeValidationError | null => {
  if (!isTreatmentPlanPriceRangeEnabled(item.isPriceRange)) return null

  if (item.minSalePrice === null || item.minSalePrice === undefined || String(item.minSalePrice).trim() === '') {
    return 'MIN_REQUIRED'
  }
  const min = toFiniteNumber(item.minSalePrice)
  if (min === null) return 'MIN_INVALID'
  if (min < 0) return 'MIN_NEGATIVE'

  if (item.maxSalePrice === null || item.maxSalePrice === undefined || String(item.maxSalePrice).trim() === '') {
    return 'MAX_REQUIRED'
  }
  const max = toFiniteNumber(item.maxSalePrice)
  if (max === null) return 'MAX_INVALID'
  if (max < 0) return 'MAX_NEGATIVE'

  if (min > max) {
    return 'MIN_GREATER_THAN_MAX'
  }
  return null
}

export const normalizeTreatmentPlanPriceRange = (item: TreatmentPlanPriceRangeInput) => {
  if (!isTreatmentPlanPriceRangeEnabled(item.isPriceRange)) {
    return {
      isPriceRange: '0',
      price: toFiniteNumber(item.price),
      minSalePrice: null,
      maxSalePrice: null,
    }
  }

  const error = validateTreatmentPlanPriceRange(item)
  if (error) throw new Error(`INVALID_TREATMENT_PLAN_PRICE_RANGE:${error}`)

  const min = toFiniteNumber(item.minSalePrice) as number
  const max = toFiniteNumber(item.maxSalePrice) as number
  return {
    isPriceRange: '1',
    price: toFiniteNumber(item.price) ?? min,
    minSalePrice: min,
    maxSalePrice: max,
  }
}

export const getTreatmentPlanPriceRange = (item: TreatmentPlanPriceRangeInput): TreatmentPlanPriceRange | null => {
  if (!isTreatmentPlanPriceRangeEnabled(item.isPriceRange)) return null
  const min = toFiniteNumber(item.minSalePrice)
  const max = toFiniteNumber(item.maxSalePrice)
  if (min === null || max === null || min < 0 || max < 0 || min > max) return null
  return { min, max }
}

export const getTreatmentPlanPriceDisplay = (item: TreatmentPlanPriceRangeInput): TreatmentPlanPriceDisplay => {
  const range = getTreatmentPlanPriceRange(item)
  const primary = toFiniteNumber(item.price) ?? range?.min ?? null
  return {
    primary,
    range: range && range.min !== range.max ? range : null,
  }
}

/** Keep the new price-range presentation scoped to the TDH theme. */
export const getTreatmentPlanPriceDisplayForTheme = (
  item: TreatmentPlanPriceRangeInput,
  isTdh: boolean,
): TreatmentPlanPriceDisplay => {
  if (!isTdh) {
    return {
      primary: toFiniteNumber(item.price),
      range: null,
    }
  }
  return getTreatmentPlanPriceDisplay(item)
}

/**
 * Use the legacy/original price for operational treatment-summary items.
 * Price ranges are document-only presentation metadata and must not change
 * the price used when an item is added to the visit.
 */
export const getTreatmentPlanOperationalPrice = (item: TreatmentPlanPriceRangeInput): number => (
  toFiniteNumber(item.price) ?? 0
)
